/**
 * The plan boundary, rendered.
 *
 * The backend answers a gated action with 402 and a structured payload. This is
 * the other half of that contract: the same information, phrased for a person
 * who wants to do their job, not for a developer reading a status code.
 *
 * Tone matters here more than anywhere else in the product. This screen appears
 * when someone tries to do something reasonable and is told no, so it says what
 * would unlock it and gets out of the way. No lock icons, no urgency, no
 * pretending the limit is a feature.
 */

import { createContext, useContext, type ReactNode } from 'react';

import { Button } from './primitives';
import './upgrade.css';

const PLAN_LABEL: Record<string, string> = {
  starter: 'Starter',
  professional: 'Professional',
  enterprise: 'Enterprise',
};

/**
 * Where "See Professional" goes, for every prompt that does not say.
 *
 * An app with a plan page provides it once at its root; one without (the till,
 * the kitchen screen) provides nothing, and its prompts carry no button rather
 * than one that leads nowhere. Before this, every prompt in the back office
 * had no button either, because not one call site passed `onUpgrade`.
 */
const UpgradeTarget = createContext<(() => void) | undefined>(undefined);
export const UpgradeTargetProvider = UpgradeTarget.Provider;

export function planLabel(key: string | undefined): string {
  if (!key) return 'a higher plan';
  return PLAN_LABEL[key] ?? key;
}

export interface UpgradePromptProps {
  /** What they were trying to do, in their words. "Add a second location". */
  action: string;
  /** Set when a numeric cap was hit rather than a missing feature. */
  cap?: number | undefined;
  currentPlan?: string | undefined;
  requiredPlan?: string | undefined;
  onUpgrade?: (() => void) | undefined;
  compact?: boolean | undefined;
}

export function UpgradePrompt({
  action,
  cap,
  currentPlan,
  requiredPlan,
  onUpgrade,
  compact = false,
}: UpgradePromptProps) {
  const fallback = useContext(UpgradeTarget);
  const upgrade = onUpgrade ?? fallback;
  const target = planLabel(requiredPlan);
  const current = planLabel(currentPlan);

  // A limit has something to add: which number you are on and that the next
  // plan raises it. A missing feature does not — "X is part of Professional"
  // under a heading reading "X needs Professional" is the same sentence twice,
  // and this component appears on a dozen screens. So the second line only
  // exists when it carries a second fact.
  // `planLabel` returns "a higher plan" when no specific plan is named, which
  // is right mid-sentence and wrong at the start of one — "Professional
  // includes 5. a higher plan raises the limit." So it is capitalised for the
  // position it is actually used in.
  const raiser = target.charAt(0).toUpperCase() + target.slice(1);
  const explanation =
    cap === undefined ? null : `${current} includes ${cap}. ${raiser} raises the limit.`;

  return (
    <div className={`up-prompt${compact ? ' up-prompt--compact' : ''}`} role="note">
      <div className="up-prompt__text">
        <p className="up-prompt__head">
          {action} needs {target}
        </p>
        {explanation && <p className="up-prompt__body">{explanation}</p>}
      </div>
      {upgrade && (
        <Button variant="primary" onClick={upgrade}>
          See {target}
        </Button>
      )}
    </div>
  );
}

/**
 * Inline nudge for a control the plan does not include.
 *
 * Pair this with `<Gated fallback={...}>` on primary actions. Hiding the
 * control entirely is right for navigation and wrong here: a button that
 * silently disappears reads as a bug, an explained one reads as a plan.
 */
export function LockedAction({
  label,
  requiredPlan,
  onUpgrade,
}: {
  label: string;
  requiredPlan?: string | undefined;
  onUpgrade?: (() => void) | undefined;
}): ReactNode {
  const fallback = useContext(UpgradeTarget);
  return (
    <button type="button" className="up-locked" onClick={onUpgrade ?? fallback}>
      <span>{label}</span>
      <span className="up-locked__tag">{planLabel(requiredPlan)}</span>
    </button>
  );
}
